import { Button, Input, Spin, message, Popconfirm, Tag } from "antd";
import { CheckCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    getOAuthAccounts,
    connectOAuthAccount,
    updateOAuthAccount,
    deleteOAuthAccount,
    getTiktokLoginUrl,
} from "../../../api/OAuthAccount";
import type { OAuthAccountItem } from "../../../api/OAuthAccount";

/**
 * Kết nối TikTok Business Messaging.
 *
 * Form thêm/sửa dùng chung luồng với Facebook (xem ConnectFacebookView), chỉ khác
 * provider gửi lên là "tiktok":
 *  1. Khai báo tài khoản OAuth  (POST /be/oauth-account | PUT /be/oauth-account/{id})
 *  2. Lấy URL cấp quyền         (GET  /be/oauth-account/{id}/tiktok-login-url)
 *  3. Điều hướng sang auth_url; backend xử lý xong redirect về /tiktok/callback
 *     kèm status / account_id / connection_id / message.
 */
/** API đã bỏ token khỏi phản hồi, chỉ còn cờ has_*; bản API cũ chưa có cờ thì nhìn token */
const hasToken = (a: OAuthAccountItem) => a.has_access_token ?? !!a.access_token;
const hasSecret = (a: OAuthAccountItem) => a.has_app_secret ?? !!a.app_secret;

export default function Messaging() {
    const { t } = useTranslation();
    const [accounts, setAccounts] = useState<OAuthAccountItem[]>([]);
    const [fetching, setFetching] = useState(true);
    const [loading, setLoading] = useState(false);
    const [authorizingId, setAuthorizingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editingAccount, setEditingAccount] = useState<OAuthAccountItem | null>(null);
    const [name, setName] = useState("");
    const [accessToken, setAccessToken] = useState("");
    const [appSecret, setAppSecret] = useState("");
    const [appId, setAppId] = useState("");

    useEffect(() => {
        fetchTiktokAccounts();
    }, []);

    const fetchTiktokAccounts = async () => {
        setFetching(true);
        try {
            // API trả cả facebook/zalo/... nên lọc lại theo provider
            const res = await getOAuthAccounts({ search: "tiktok" });
            if (res.status && res.data?.items) {
                setAccounts(
                    res.data.items.filter((a) =>
                        String(a.provider || "").toLowerCase().includes("tiktok")
                    )
                );
            }
        } catch {
            // lỗi mạng -> giữ danh sách rỗng, người dùng bấm kết nối lại
        } finally {
            setFetching(false);
        }
    };

    const resetForm = () => {
        setEditingAccount(null);
        setName("");
        setAccessToken("");
        setAppSecret("");
        setAppId("");
    };

    const handleAddNew = () => {
        resetForm();
        setShowForm(true);
    };

    const handleEdit = (account: OAuthAccountItem) => {
        setEditingAccount(account);
        setName(account.name || "");
        setAccessToken("");
        setAppSecret("");
        setAppId(account.app_id ? String(account.app_id) : "");
        setShowForm(true);
    };

    const handleCancelForm = () => {
        setShowForm(false);
        resetForm();
    };

    /** Tạo mới / cập nhật tài khoản OAuth với provider "tiktok" */
    const handleConnect = async () => {
        // Đọc trước khi resetForm() xoá state: nhánh tạo mới còn đi tiếp bước cấp quyền
        const isCreate = !editingAccount;
        setLoading(true);
        try {
            const res = editingAccount
                ? await updateOAuthAccount(
                    editingAccount.id,
                    "tiktok",
                    accessToken || undefined,
                    appSecret || undefined,
                    appId || undefined,
                    name || undefined
                )
                : await connectOAuthAccount(
                    "tiktok",
                    accessToken || undefined,
                    appSecret || undefined,
                    appId || undefined,
                    name || undefined
                );
            if (!res.status) {
                message.error(res.msg || t("chats.connect_failed"));
                return;
            }

            const newId = Number(res?.data?.id);
            message.success(isCreate ? t("chats.connect_success") : t("chats.update_success"));
            setShowForm(false);
            resetForm();

            // Đúng luồng tài liệu: tạo tài khoản xong thì đi thẳng bước 2-3
            // (lấy auth_url rồi điều hướng). Sửa tài khoản chỉ nạp lại danh sách,
            // người dùng tự bấm "Cấp quyền lại" khi cần.
            if (isCreate && newId) {
                const ok = await goToAuthorize(newId);
                if (ok) return; // đang rời trang, khỏi cập nhật state nữa
            }
            fetchTiktokAccounts();
        } catch {
            message.error(t("chats.connect_failed"));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Bước 2-3: GET /be/oauth-account/{id}/tiktok-login-url rồi điều hướng cả tab
     * sang auth_url. Trả về false khi không lấy được link để nơi gọi xử lý tiếp.
     */
    const goToAuthorize = async (accountId: number): Promise<boolean> => {
        setAuthorizingId(accountId);
        const res = await getTiktokLoginUrl(accountId);
        if (!res.status || !res.authUrl) {
            setAuthorizingId(null);
            message.error(res.msg || t("chats.tiktok_authorize_failed") || "Không lấy được liên kết cấp quyền TikTok");
            return false;
        }
        window.location.href = res.authUrl;
        return true;
    };

    const handleDelete = async (account: OAuthAccountItem) => {
        setDeletingId(account.id);
        try {
            const res = await deleteOAuthAccount(account.id);
            if (res.status) {
                message.success(t("chats.delete_success"));
                fetchTiktokAccounts();
            } else {
                message.error(res.msg || t("chats.delete_failed"));
            }
        } finally {
            setDeletingId(null);
        }
    };

    /** Form khai báo tài khoản — dùng chung cho thêm mới và chỉnh sửa */
    const renderForm = () => (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
            <div className="flex flex-col items-center text-center">
                <div className="flex items-center gap-6 mb-6">
                    <div className="w-[48px] h-[48px] bg-white rounded-full shadow-md flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xl">
                            <img src="https://khothi.buso.asia/uploads/2025/10/photo_library/i0sGPLyOtv3a2fb45cb6264d9f6848b54d45c8a57f.png" alt="Buso" className="w-full h-full object-contain" />
                        </div>
                    </div>
                    <div className="icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#3e91ab" viewBox="0 0 256 256"><path d="M24,128A72.08,72.08,0,0,1,96,56H204.69L194.34,45.66a8,8,0,0,1,11.32-11.32l24,24a8,8,0,0,1,0,11.32l-24,24a8,8,0,0,1-11.32-11.32L204.69,72H96a56.06,56.06,0,0,0-56,56,8,8,0,0,1-16,0Zm200-8a8,8,0,0,0-8,8,56.06,56.06,0,0,1-56,56H51.31l10.35-10.34a8,8,0,0,0-11.32-11.32l-24,24a8,8,0,0,0,0,11.32l24,24a8,8,0,0,0,11.32-11.32L51.31,200H160a72.08,72.08,0,0,0,72-72A8,8,0,0,0,224,120Z"></path></svg></div>
                    <div className="w-[48px] h-[48px] !rounded-full flex items-center justify-center text-white text-3xl shadow-md overflow-hidden">
                        <img src="https://pancake.vn/static/images/platform-connection/tiktok.png" alt="TikTok" className="w-full h-full object-contain" />
                    </div>
                </div>

                <h2 className="text-xl font-semibold text-gray-800 mb-3">
                    {editingAccount ? t("chats.edit_buso_tiktok") : t("chats.connect_buso_tiktok")}
                </h2>
                <p className="text-gray-500 max-w-[340px] text-sm mb-6 leading-relaxed">
                    {t("chats.tiktok_msg_desc")}
                </p>

                {/* Chrome coi cặp "ô text + ô password" là form đăng nhập rồi tự điền
                    email/mật khẩu đã lưu vào Access Token / App Secret -> chặn bằng
                    name lạ + autoComplete + ô mồi ẩn (giống bên Facebook) */}
                <div className="w-[340px] flex flex-col gap-3 mb-6 text-left">
                    <input
                        type="text"
                        name="tiktok-autofill-decoy"
                        autoComplete="username"
                        tabIndex={-1}
                        aria-hidden="true"
                        className="hidden"
                    />
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">{t("chats.name")}</label>
                        <Input
                            name="tiktok-name"
                            autoComplete="off"
                            placeholder={t("chats.input_name")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">App ID</label>
                        <Input
                            name="tiktok-app-id"
                            autoComplete="off"
                            placeholder={t("chats.input_app_id")}
                            value={appId}
                            onChange={(e) => setAppId(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Access Token</label>
                        <Input
                            name="tiktok-access-token"
                            autoComplete="off"
                            placeholder={editingAccount ? "Để trống nếu không đổi" : t("chats.input_access_token")}
                            value={accessToken}
                            onChange={(e) => setAccessToken(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">App Secret</label>
                        <Input.Password
                            name="tiktok-app-secret"
                            autoComplete="new-password"
                            placeholder={editingAccount ? (hasSecret(editingAccount) ? "Đã có, để trống nếu không đổi" : "Để trống nếu không đổi") : t("chats.input_app_secret")}
                            value={appSecret}
                            onChange={(e) => setAppSecret(e.target.value)}
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    <Button size="large" onClick={handleCancelForm} className="h-[42px]">
                        {t("chats.cancel")}
                    </Button>
                    <Button
                        size="large"
                        loading={loading}
                        onClick={handleConnect}
                        className="
                            w-[240px] h-[42px]
                            flex items-center justify-center gap-2
                            border border-gray-300
                            hover:!border-[#3e91ab]
                            bg-[#3e91ab] hover:!bg-[#357f96] hover:border-[#357f96]
                            text-white hover:!text-white
                        "
                    >
                        <div className="w-[20px] h-[20px]">
                            <img className="platform-icon-img tiktok rounded-[4px]" src="https://pancake.vn/static/images/platform-connection/tiktok.png" alt="tiktok" />
                        </div>
                        <span className="font-medium">
                            {editingAccount ? t("chats.update_tiktok") : t("chats.connect_tiktok")}
                        </span>
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Banner ưu đãi quảng cáo của TikTok */}
            <div className="flex-shrink-0 w-full border-t-4 border-cyan-400 bg-cyan-50 px-4 py-3 flex items-center justify-between flex-col md:flex-row gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center">
                        <img
                            src="https://pancake.vn/static/images/platform-connection/tiktok.png"
                            alt="TikTok"
                            className="w-10 h-10 object-contain"
                        />
                    </div>
                    <div>
                        <div className="font-medium text-gray-800">{t('chats.tiktok_ad_promo')}</div>
                        <div className="text-sm text-gray-600 mt-1">{t('chats.tiktok_ad_levels')}</div>
                    </div>
                </div>
            </div>

            {/* Form + danh sách cuộn trong khung, tránh tràn ra ngoài modal */}
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                <Spin spinning={fetching}>
                    {showForm && <div className="pt-6">{renderForm()}</div>}

                {accounts.length > 0 ? (
                    /* ĐÃ CÓ TÀI KHOẢN: liệt kê để cấp quyền lại / sửa / xóa / thêm mới */
                    <div className={`pb-6 space-y-3 ${showForm ? "" : "pt-6"}`}>
                        {accounts.map((account) => (
                            <div
                                key={account.id}
                                className="flex flex-col md:flex-row md:items-center justify-between gap-3 border border-gray-200 rounded-xl px-4 py-3"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                                        <img
                                            src="https://pancake.vn/static/images/platform-connection/tiktok.png"
                                            alt="TikTok"
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-medium text-gray-800 truncate">
                                            {account.name || `TikTok #${account.id}`}
                                        </div>
                                        <div className="text-[12px] text-gray-500 flex items-center gap-2">
                                            <span>ID: {account.id}</span>
                                            {hasToken(account) ? (
                                                <Tag color="green" className="mr-0">
                                                    <CheckCircleFilled className="mr-1" />
                                                    {t('chats.tiktok_authorized') || "Đã cấp quyền"}
                                                </Tag>
                                            ) : (
                                                <Tag color="orange" className="mr-0">
                                                    {t('chats.tiktok_not_authorized') || "Chưa cấp quyền"}
                                                </Tag>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <Button
                                        icon={<SafetyCertificateOutlined />}
                                        loading={authorizingId === account.id}
                                        onClick={() => goToAuthorize(account.id)}
                                    >
                                        {hasToken(account)
                                            ? (t('chats.tiktok_reauthorize') || "Cấp quyền lại")
                                            : (t('chats.tiktok_authorize') || "Cấp quyền")}
                                    </Button>
                                    <Button
                                        type="text"
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => handleEdit(account)}
                                    />
                                    <Popconfirm
                                        title={t('chats.tiktok_delete_confirm') || "Xóa tài khoản TikTok này?"}
                                        okText={t('cd.delete') || "Xóa"}
                                        cancelText={t('cd.cancel') || "Hủy"}
                                        okButtonProps={{ danger: true, loading: deletingId === account.id }}
                                        onConfirm={() => handleDelete(account)}
                                    >
                                        <Button danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                </div>
                            </div>
                        ))}

                        {!showForm && (
                            <Button
                                icon={<PlusOutlined />}
                                onClick={handleAddNew}
                                className="w-full md:w-auto"
                            >
                                {t('chats.tiktok_add_account') || "Thêm tài khoản TikTok khác"}
                            </Button>
                        )}
                    </div>
                ) : !showForm ? (
                    /* CHƯA CÓ TÀI KHOẢN: màn hình giới thiệu + nút mở form khai báo */
                    <div className="flex flex-col items-center justify-center text-center py-12">
                        <div className="flex items-center gap-6 mb-6">
                            <div className="w-[48px] h-[48px] bg-white rounded-full shadow-md flex items-center justify-center">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xl">
                                    <img src="https://khothi.buso.asia/uploads/2025/10/photo_library/i0sGPLyOtv3a2fb45cb6264d9f6848b54d45c8a57f.png" alt="Buso" className="w-full h-full object-contain" />
                                </div>
                            </div>

                            <div className="icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#3e91ab" viewBox="0 0 256 256"><path d="M24,128A72.08,72.08,0,0,1,96,56H204.69L194.34,45.66a8,8,0,0,1,11.32-11.32l24,24a8,8,0,0,1,0,11.32l-24,24a8,8,0,0,1-11.32-11.32L204.69,72H96a56.06,56.06,0,0,0-56,56,8,8,0,0,1-16,0Zm200-8a8,8,0,0,0-8,8,56.06,56.06,0,0,1-56,56H51.31l10.35-10.34a8,8,0,0,0-11.32-11.32l-24,24a8,8,0,0,0,0,11.32l24,24a8,8,0,0,0,11.32-11.32L51.31,200H160a72.08,72.08,0,0,0,72-72A8,8,0,0,0,224,120Z"></path></svg></div>

                            <div className="w-[48px] h-[48px] !rounded-full flex items-center justify-center text-white text-3xl shadow-md overflow-hidden">
                                <img src="https://pancake.vn/static/images/platform-connection/tiktok.png" alt="TikTok" className="w-full h-full object-contain" />
                            </div>
                        </div>

                        <h2 className="text-xl font-semibold text-gray-800 mb-3">
                            {t('chats.connect_buso_tiktok')}
                        </h2>

                        <p className="text-gray-500 max-w-[340px] text-sm mb-8 leading-relaxed">
                            {t('chats.tiktok_msg_desc')}
                        </p>

                        <Button
                            size="large"
                            onClick={handleAddNew}
                            className="
                                w-[340px] h-[42px]
                                flex items-center justify-center gap-2
                                border border-gray-300
                                hover:!border-[#3e91ab]
                                bg-[#3e91ab] hover:!bg-[#357f96] hover:border-[#357f96]
                                text-white hover:!text-white
                            "
                        >
                            <div className="w-[20px] h-[20px]">
                                <img className="platform-icon-img tiktok rounded-[4px]" src="https://pancake.vn/static/images/platform-connection/tiktok.png" alt="tiktok" />
                            </div>
                            <span className="font-medium">{t('chats.connect_tiktok')}</span>
                        </Button>

                        {/* Ghi chú khu vực không hỗ trợ + link hướng dẫn */}
                        <div className="flex items-start gap-2 text-sm text-gray-600 max-w-[340px] mt-4">
                            <div className="mt-[2px] text-gray-500">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                                    <path d="M140,180a12,12,0,1,1-12-12A12,12,0,0,1,140,180ZM128,72c-22.06,0-40,16.15-40,36v4a8,8,0,0,0,16,0v-4c0-11,10.77-20,24-20s24,9,24,20-10.77,20-24,20a8,8,0,0,0-8,8v8a8,8,0,0,0,16,0v-.72c18.24-3.35,32-17.9,32-35.28C168,88.15,150.06,72,128,72Zm104,56A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z" />
                                </svg>
                            </div>
                            <div className="text-left">
                                <span>{t('chats.tiktok_region_note')}{" "}</span>
                                <a
                                    id="tiktok-business-help"
                                    href="https://pke.gg/493xj5ay"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#357f96] hover:underline"
                                >
                                    {t('chats.guide_link')}
                                </a>
                            </div>
                        </div>
                    </div>
                ) : null}
                </Spin>
            </div>
        </div>
    );
}
